import type { CSSProperties } from 'react';

import { TYPOGRAPHY } from './styles';

// ============================================================================
// CSS Variable Helper — single source of truth for inline styles
// ============================================================================

/** Helper to wrap CSS variable with hsl() for use in inline styles */
export const hsl = (varName: string) => `hsl(var(--${varName}))` as const;

/** Common CSS colors for inline styles — prevents typos and ensures consistency */
export const CSS_COLORS = {
  // Foreground colors
  foreground: hsl('foreground'),
  mutedForeground: hsl('muted-foreground'),
  primaryForeground: hsl('primary-foreground'),

  // Background colors
  background: hsl('background'),
  card: hsl('card'),
  muted: hsl('muted'),

  // Accent colors
  primary: hsl('primary'),
  accent: hsl('accent'),

  // Border & misc
  border: hsl('border'),
} as const;

// ============================================================================
// PAGE STYLES
// ============================================================================

export const PAGE_STYLES = {
  /** Main page title */
  title: {
    color: hsl('foreground'),
    fontFamily: `${TYPOGRAPHY.fontFamily}, sans-serif`,
    fontWeight: TYPOGRAPHY.fontWeight.bold,
    fontSize: TYPOGRAPHY.fontSize['2xl'],
  } as CSSProperties,

  /** Page description/subtitle */
  description: {
    color: hsl('muted-foreground'),
    fontFamily: `${TYPOGRAPHY.fontFamily}, sans-serif`,
    fontWeight: TYPOGRAPHY.fontWeight.normal,
    fontSize: TYPOGRAPHY.fontSize.md,
    marginTop: '4px',
  } as CSSProperties,

  /** Section title within cards */
  sectionTitle: {
    color: hsl('foreground'),
    fontFamily: `${TYPOGRAPHY.fontFamily}, sans-serif`,
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    fontSize: TYPOGRAPHY.fontSize.lg,
  } as CSSProperties,

  /** Form field label */
  label: {
    fontFamily: `${TYPOGRAPHY.fontFamily}, sans-serif`,
    fontSize: TYPOGRAPHY.fontSize.md,
    fontWeight: TYPOGRAPHY.fontWeight.semibold,
    color: hsl('muted-foreground'),
    marginBottom: '6px',
    display: 'block',
  } as CSSProperties,

  /** Required asterisk style */
  labelRequired: {
    color: hsl('destructive'),
    marginLeft: '2px',
  } as CSSProperties,
} as const;

// ============================================================================
// SECTION HEADER STYLES
// ============================================================================

export const SECTION_HEADER = {
  /** Container style for card section headers */
  container: {
    height: '56px',
    gap: '10px',
    paddingTop: '16px',
    paddingRight: '24px',
    paddingBottom: '16px',
    paddingLeft: '24px',
  } as CSSProperties,

  /** Content padding for card sections */
  content: {
    padding: '24px',
  } as CSSProperties,
} as const;

/** Combined class for section header container */
export const SECTION_HEADER_CLASS = 'flex items-center border-b border-border';

// ============================================================================
// TABLE STYLES (consumed by DataTable, RoleDetailPage)
// ============================================================================

export const TABLE_HEADER_STYLE: CSSProperties = {
  color: hsl('muted-foreground'),
  backgroundColor: hsl('border'),
  fontFamily: `${TYPOGRAPHY.fontFamily}, sans-serif`,
  fontWeight: TYPOGRAPHY.fontWeight.semibold,
  fontSize: TYPOGRAPHY.fontSize.md,
  height: '40px',
};

export const TABLE_CELL_BASE =
  'table-cell px-4 py-0 whitespace-nowrap h-[40px] max-h-[40px]';

/** Row hover utility class (defined in index.css) */
export const ROW_HOVER_CLASS = 'row-hover';

/** Sticky cell hover utility class (defined in index.css) */
export const STICKY_CELL_CLASS = 'sticky-cell-hover';

/** Sticky header cell class (defined in index.css) */
export const STICKY_HEADER_CELL_CLASS = 'sticky-header-cell';

/** Background for sticky header cells — must be SOLID (opaque) to cover content */
export const STICKY_HEADER_BG = hsl('border');

// ============================================================================
// LAYOUT (Tailwind classes)
// ============================================================================

export const LAYOUT = {
  pageHeader: 'flex-between',
  pageHeaderLeft: 'flex-1',
  pageHeaderRight: 'flex items-center gap-2',
  card: 'rounded-lg border border-border bg-background overflow-hidden',
  tableWrapper: 'overflow-x-auto',
} as const;

export const LAYOUT2 = {
  pageHeader: 'flex-between',
  pageHeaderLeft: 'flex-1',
  pageHeaderRight: 'flex items-center gap-2',
  card: 'overflow-hidden',
  tableWrapper: 'overflow-x-auto',
} as const;
